/**
 * Feature flags for the map editor Canvas migration.
 *
 * Resolution order (first hit wins):
 *   1. localStorage override  — dev-only runtime toggle, survives reload
 *   2. runtime config         — window.ENV.<KEY> (Docker/K8s injected)
 *   3. build-time env         — process.env.REACT_APP_<KEY>
 *   4. default                — hard-coded fallback below
 *
 * Toggle at runtime from the browser console (then reload):
 *   window.__mapFlags.set('PERF_HUD', true)
 *   window.__mapFlags.set('CANVAS_EDITOR', true)
 *   window.__mapFlags.clear('CANVAS_EDITOR')
 */

export type FlagKey = 'CANVAS_EDITOR' | 'PERF_HUD'

const DEFAULTS: Record<FlagKey, boolean> = {
  // Phase 1+: render the editor with the unified Canvas renderer instead of the
  // JointJS SVG Paper. Default ON (2026-07 graduation) — set localStorage
  // mapFlag:CANVAS_EDITOR=false to fall back to the legacy SVG path.
  CANVAS_EDITOR: true,
  // Show the performance HUD (FPS / frame ms / DOM node count / named measures).
  PERF_HUD: false,
}

const LS_PREFIX = 'mapFlag:'

const runtimeConfig = (): Record<string, unknown> =>
  (typeof window !== 'undefined' && (window as any).ENV) || {}

const parseBool = (v: unknown): boolean | undefined => {
  if (v === true || v === false) return v
  if (typeof v === 'string') {
    if (v === 'true' || v === '1') return true
    if (v === 'false' || v === '0') return false
  }
  return undefined
}

export const getFlag = (key: FlagKey): boolean => {
  // 1. localStorage override (dev runtime toggle)
  if (typeof window !== 'undefined') {
    try {
      const ls = parseBool(window.localStorage.getItem(LS_PREFIX + key))
      if (ls !== undefined) return ls
    } catch {
      // localStorage may be unavailable (privacy mode) — ignore.
    }
  }
  // 2. runtime injected config
  const rt = parseBool(runtimeConfig()[key])
  if (rt !== undefined) return rt
  // 3. build-time env
  const env = parseBool(process.env[`REACT_APP_${key}`])
  if (env !== undefined) return env
  // 4. default
  return DEFAULTS[key]
}

// Dev-only console helpers.
if (typeof window !== 'undefined') {
  ;(window as any).__mapFlags = {
    get: getFlag,
    set: (key: FlagKey, value: boolean) => {
      try {
        window.localStorage.setItem(LS_PREFIX + key, String(value))
      } catch {
        /* ignore */
      }
      // eslint-disable-next-line no-console
      console.info(`[mapFlags] ${key} = ${value} (reload to apply)`)
    },
    clear: (key: FlagKey) => {
      try {
        window.localStorage.removeItem(LS_PREFIX + key)
      } catch {
        /* ignore */
      }
      // eslint-disable-next-line no-console
      console.info(`[mapFlags] ${key} cleared (reload to apply)`)
    },
  }
}
